import { EXTERNAL_CLADDING_PRODUCTS, INDOOR_CLADDING_PRODUCTS, CLADDING_ACCESSORIES } from "@/data/wpcCladdingProducts";
import { PS_CLADDING_PRODUCTS } from "@/data/psCladdingProducts";
import { ACOUSTIC_CLADDING_PRODUCTS } from "@/data/acousticCladdingProducts";
import { PVC_WET_WALL_PRODUCTS, WET_WALL_ACCESSORIES } from "@/data/pvcWetWallProducts";
import { SPC_FLOORING_PRODUCTS } from "@/data/spcFlooringProducts";
import { UV_MARBLE_SHEET_PRODUCTS } from "@/data/uvMarbleSheetProducts";
import { FLEXIBLE_WALL_CLADDING_PRODUCTS } from "@/data/flexibleWallCladdingProducts";
import { HEDGE_PRODUCTS } from "@/data/hedgeProducts";
import { GARDEN_PEBBLE_PRODUCTS } from "@/data/gardenPebbleProducts";
import { GRASS_PRODUCTS } from "@/data/grassProducts";

export type SearchCategory = "product" | "resource" | "page" | "location";

export interface SearchEntry {
  id: string;
  title: string;
  description: string;
  category: SearchCategory;
  href: string;
  /** Extra terms used for matching (not displayed) */
  keywords?: string;
}

/** Build product entries dynamically from data files so every product is searchable. */
function buildProductEntries(): SearchEntry[] {
  const entries: SearchEntry[] = [];

  // WPC External Cladding
  EXTERNAL_CLADDING_PRODUCTS.forEach((p) => {
    entries.push({
      id: `ext-${p.id}`,
      title: p.name,
      description: p.description,
      category: "product",
      href: `/wpc-external-cladding/product/${p.id}`,
      keywords: `wpc external cladding ${p.colour} ${p.features.join(" ")}`,
    });
  });

  // Indoor (WPC) Cladding — hub redirects, route to hub
  INDOOR_CLADDING_PRODUCTS.forEach((p) => {
    entries.push({
      id: `ind-${p.id}`,
      title: p.name,
      description: p.description,
      category: "product",
      href: `/indoor-cladding`,
      keywords: `indoor cladding ${p.colour} ${p.features.join(" ")}`,
    });
  });

  // Cladding Accessories
  CLADDING_ACCESSORIES.forEach((a) => {
    entries.push({
      id: `acc-${a.id}`,
      title: a.name,
      description: a.description,
      category: "product",
      href: `/cladding-accessories/${a.id}`,
      keywords: `cladding accessory trim ${a.size}`,
    });
  });

  // PS Cladding
  PS_CLADDING_PRODUCTS.forEach((p) => {
    entries.push({
      id: `ps-${p.id}`,
      title: `${p.name} PS Cladding`,
      description: p.description,
      category: "product",
      href: `/ps-cladding/${p.id}`,
      keywords: `ps cladding indoor wall panel ${p.colour}`,
    });
  });

  // Acoustic Cladding
  ACOUSTIC_CLADDING_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `ac-${p.id}`,
      title: p.name,
      description: p.description ?? "Acoustic slat wall panel",
      category: "product",
      href: `/acoustic-cladding/${p.id}`,
      keywords: `acoustic slat wall panel ${p.colour ?? ""}`,
    });
  });

  // PVC Wet Wall
  PVC_WET_WALL_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `ww-${p.id}`,
      title: p.name,
      description: p.description ?? "PVC wet wall panel",
      category: "product",
      href: `/pvc-wet-wall-panels/${p.id}`,
      keywords: `pvc wet wall bathroom shower waterproof ${p.colour ?? ""}`,
    });
  });
  WET_WALL_ACCESSORIES.forEach((a: any) => {
    entries.push({
      id: `wwa-${a.id}`,
      title: a.name,
      description: a.description ?? "Wet wall accessory",
      category: "product",
      href: `/wet-wall-accessories/${a.id}`,
      keywords: `wet wall accessory trim`,
    });
  });

  // SPC / Hybrid Flooring
  SPC_FLOORING_PRODUCTS.forEach((p) => {
    entries.push({
      id: `spc-${p.id}`,
      title: `${p.name} Hybrid Flooring`,
      description: p.description,
      category: "product",
      href: `/hybrid-flooring/${p.id}`,
      keywords: `spc hybrid flooring ${p.range} ${p.colour}`,
    });
  });

  // UV Marble Sheets
  UV_MARBLE_SHEET_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `uv-${p.id}`,
      title: p.name,
      description: p.description ?? "UV marble sheet panel",
      category: "product",
      href: `/uv-marble-sheets/${p.id}`,
      keywords: `uv marble sheet panel ${p.colour ?? ""}`,
    });
  });

  // Flexible Wall Cladding
  FLEXIBLE_WALL_CLADDING_PRODUCTS.forEach((p) => {
    entries.push({
      id: `fwc-${p.id}`,
      title: p.name,
      description: p.description,
      category: "product",
      href: `/flexible-wall-cladding/${p.id}`,
      keywords: `flexible wall cladding timber look curved wall panel indoor cladding ${p.colour}`,
    });
  });

  // Hedges
  HEDGE_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `hedge-${p.id}`,
      title: p.name,
      description: p.description ?? "Artificial hedge panel",
      category: "product",
      href: `/hedges/${p.id}`,
      keywords: `hedge privacy panel artificial`,
    });
  });

  // Garden Pebbles
  GARDEN_PEBBLE_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `peb-${p.id}`,
      title: p.name,
      description: p.description ?? "Decorative garden pebbles",
      category: "product",
      href: `/garden-pebbles/${p.id}`,
      keywords: `garden pebbles landscaping decorative stones`,
    });
  });

  // Artificial Grass
  GRASS_PRODUCTS.forEach((p: any) => {
    entries.push({
      id: `grass-${p.id}`,
      title: p.name,
      description: p.description ?? "Artificial grass turf",
      category: "product",
      href: `/artificial-grass`,
      keywords: `artificial grass turf lawn`,
    });
  });

  return entries;
}

const STATIC_ENTRIES: SearchEntry[] = [
  // Resources
  { id: "r1", title: "Installation Guides", description: "Step-by-step installation instructions for all products", category: "resource", href: "/resources/guides" },
  { id: "r2", title: "Area Calculator", description: "Calculate material requirements for your project", category: "resource", href: "/resources/calculator" },
  { id: "r3", title: "Resources Hub", description: "Guides, tutorials and documentation", category: "resource", href: "/resources" },

  // Pages
  { id: "pg1", title: "Shop All Products", description: "Browse the full Supreme Turf catalogue", category: "page", href: "/shop" },
  { id: "pg2", title: "Trade / B2B Quotes", description: "Get bulk pricing and project quotes", category: "page", href: "/quote" },
  { id: "pg3", title: "Bulk Quote", description: "Get pricing for large orders", category: "page", href: "/bulk-quote" },
  { id: "pg4", title: "About Us", description: "Learn about Supreme Turf", category: "page", href: "/about" },
  { id: "pg5", title: "Contact Us", description: "Get in touch with our team", category: "page", href: "/contact" },
  
  { id: "pg7", title: "Our Picks Gallery", description: "Real installations and inspiration", category: "page", href: "/our-picks" },
  { id: "pg8", title: "Blog", description: "Tips, guides and product news", category: "page", href: "/blog" },

  // Category landing pages
  { id: "cat1", title: "Artificial Grass", description: "Premium artificial turf range", category: "page", href: "/artificial-grass", keywords: "grass turf lawn" },
  { id: "cat2", title: "WPC Decking", description: "Composite decking boards", category: "page", href: "/wpc-decking", keywords: "deck decking composite" },
  { id: "cat3", title: "WPC External Cladding", description: "Exterior wall cladding panels", category: "page", href: "/wpc-external-cladding", keywords: "wall cladding exterior" },
  { id: "cat4", title: "Indoor Cladding", description: "Indoor wall cladding range", category: "page", href: "/indoor-cladding", keywords: "indoor wall cladding" },
  { id: "cat5", title: "PS Cladding", description: "PS indoor wall panels", category: "page", href: "/ps-cladding", keywords: "ps polystyrene indoor wall" },
  { id: "cat6", title: "Acoustic Cladding", description: "Acoustic slat wall panels", category: "page", href: "/acoustic-cladding", keywords: "acoustic slat wall" },
  { id: "cat7", title: "PVC Wet Wall Panels", description: "Waterproof bathroom & shower wall panels", category: "page", href: "/pvc-wet-wall-panels", keywords: "pvc wet wall bathroom shower waterproof" },
  { id: "cat8", title: "Hybrid Flooring (SPC)", description: "Waterproof SPC click-lock flooring", category: "page", href: "/hybrid-flooring", keywords: "spc hybrid flooring vinyl" },
  { id: "cat9", title: "UV Marble Sheets", description: "Marble-effect wall sheets", category: "page", href: "/uv-marble-sheets", keywords: "uv marble sheet wall" },
  { id: "cat14", title: "Flexible Wall Cladding", description: "Timber-look flexible interior wall panels", category: "page", href: "/flexible-wall-cladding", keywords: "flexible wall cladding curved timber look feature wall panels" },
  { id: "cat10", title: "Artificial Hedges", description: "Privacy hedge panels", category: "page", href: "/hedges", keywords: "hedge privacy panels" },
  { id: "cat11", title: "Garden Pebbles", description: "Decorative landscaping pebbles", category: "page", href: "/garden-pebbles", keywords: "pebbles stones landscaping" },
  { id: "cat12", title: "Weed Mats", description: "Weed control matting", category: "page", href: "/weed-mats", keywords: "weed mat landscape fabric" },
  { id: "cat13", title: "Accessories", description: "Trims, fixings and accessories", category: "page", href: "/accessories" },

  // Locations
  { id: "l1", title: "Virginia, QLD Branch", description: "Unit 3/7 Lathe St, Virginia QLD 4014", category: "location", href: "/locations/virginia" },
  { id: "l2", title: "Darra, QLD Branch", description: "6 Fienta Pl, Darra QLD 4076", category: "location", href: "/locations/darra" },
  { id: "l3", title: "Mount Druitt, NSW Branch", description: "Unit 11/8 Wainwright Rd, Mount Druitt NSW 2770", category: "location", href: "/locations/mount-druitt" },
  { id: "l4", title: "Ridleyton, SA Branch", description: "153 South Road, Ridleyton SA 5008", category: "location", href: "/locations/ridleyton" },
  { id: "l5", title: "Truganina, VIC Branch", description: "39A Apex Drive, Truganina VIC 3029", category: "location", href: "/locations/truganina" },
];

export const SEARCH_INDEX: SearchEntry[] = [
  ...buildProductEntries(),
  ...STATIC_ENTRIES,
];

/** Token-based fuzzy matcher: every word in the query must appear somewhere in the entry. */
export function searchEntries(query: string, category: SearchCategory | null, limit = 20): SearchEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);

  const matches = SEARCH_INDEX.filter((entry) => {
    if (category && entry.category !== category) return false;
    const hay = `${entry.title} ${entry.description} ${entry.keywords ?? ""}`.toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });

  // Rank: title-prefix match > title contains > other
  matches.sort((a, b) => {
    const at = a.title.toLowerCase();
    const bt = b.title.toLowerCase();
    const ap = at.startsWith(q) ? 0 : at.includes(q) ? 1 : 2;
    const bp = bt.startsWith(q) ? 0 : bt.includes(q) ? 1 : 2;
    return ap - bp;
  });

  return matches.slice(0, limit);
}
